import React, { useContext, useMemo } from 'react';
import { PaymentGatewayType } from 'app/common/constants';
import { getPaymentRequestValues } from 'app/common/hooks/payment/paymentUtil.js';
import { getCartHeaders } from 'app/common/utils/RequestUtils';
import {
  useCartInfo,
  useFormatMessage,
  useHistory,
  useModifyCartRequest
} from 'app/common/hooks/index.js';
import { CartContext } from 'app/common/contexts';
import PaymentForm from '../PaymentForm/index.js';
import {
  DecoratedField,
  Icon,
  SecondaryButton
} from 'app/common/components/index.js';
import { get } from 'lodash';
import { PAYMENT_INFO_PATH } from '../../../../CheckoutPaths.js';
import messages from './AuroraPayPaymentInfoStage.messages.js';
import { default as messages2 } from 'app/checkout/components/CheckoutLayout/components/FulfillmentInfoWithOptions/FulfillmentInfoWithOptions.messages';
import { CheckoutContext } from 'app/checkout/contexts/index.js';
import { AuroraPayImageWhite } from 'app/checkout/utils/PaymentMethodUtil.js';

const AuroraPayPaymentInfoStage = ({ active = false, completed = false }) => {
  return active ? (
    <AuroraPayPaymentInfoEditable active={active} completed={completed} />
  ) : (
    <AuroraPayPaymentInfoReadOnly />
  );
};

const AuroraPayPaymentInfoEditable = ({ active = false, completed }) => {
  const { cart, setCart } = useContext(CartContext);
  const {
    checkoutOperations: { baseUrl }
  } = useCartInfo();
  const { sendCallback: updateCartInfo } = useModifyCartRequest(
    `${baseUrl}/${cart.id}`,
    null,
    false
  );

  const cartHeaders = getCartHeaders(cart.version);

  const handleSubmit = async (values, actions, addlInfo) => {
    const { attributes, paymentType, gatewayType, paymentMethodProperties } =
      values;

    const updatedCart = await updateCartInfo({
      method: 'patch',
      data: {
        attributes: {
          PAY_IN_STORE: false,
          SUBMIT_VER: cart.version,
          DEALER_LOCATION_ID: values.dealerLocation
        },
        purchaseOrderNumber: values.purchaseOrderNumber
      },
      headers: cartHeaders
    });
    setCart(updatedCart);

    const { sendPaymentRequest, method, existingPayment } = addlInfo;
    const amounts = getPaymentRequestValues(updatedCart);
    const existingPaymentId = get(existingPayment, 'id');

    const request = {
      method,
      attributes: {
        ...attributes,
        PAY_IN_STORE: false,
        VENDOR_CODE: values.dealerLocation,
        CUSTOMER_CODE: values.fleetLocationId,
        PO_NUMBER: values.purchaseOrderNumber,
        cart: JSON.stringify(updatedCart)
      },
      type: paymentType,
      gatewayType,
      paymentMethodProperties,
      shouldArchiveExistingPayments: true,
      ...amounts
    };
    return await sendPaymentRequest(request, existingPaymentId);
  };

  const { isSubmittingOrder } = useContext(CheckoutContext);
  const formatMessage = useFormatMessage();

  return (
    <>
      <PaymentForm
        active={active}
        completed={completed}
        handleSubmit={handleSubmit}
        paymentGatewayType={AuroraPayPaymentInfoStage.Editable.GatewayType}
      >
        {formik => {
          return (
            <>
              <ErrorMessage />
              <DecoratedField
                className={'mt-2'}
                required={true}
                disabled={isSubmittingOrder || !active}
                id="purchaseOrderNumber"
                name="purchaseOrderNumber"
                maxLength={20}
                label={formatMessage(messages.poNumberFieldLabel)}
                placeholder={formatMessage(messages2.purchaseOrderNumberLabel)}
                hint={formatMessage(messages2.purchaseOrderNumberHint)}
                validate={v => {
                  if (0 === v.length || v.length > 21) {
                    return formatMessage(messages.purchaseOrderValidationError);
                  }
                  return '';
                }}
              />
            </>
          );
        }}
      </PaymentForm>
    </>
  );
};

const ErrorMessage = () => {
  const { checkoutFailureType } = useContext(CheckoutContext);
  const formatMessage = useFormatMessage();
  if (checkoutFailureType !== 'PO_NUMBER_INVALID') {
    return null;
  }
  return (
    <section className="flex items-center mb-4 px-2 py-1 text-sm text-red-600 leading-snug border border-solid border-red-300 bg-red-100 rounded md:px-4 md:py-2 lg:text-base lg:leading-normal">
      <Icon className="mr-2 md:mr-4" name="exclamation-circle" />

      <div id="general-order-error">
        {formatMessage(messages.poNumberInvalid)}
      </div>
    </section>
  );
};

AuroraPayPaymentInfoEditable.GatewayType = PaymentGatewayType.AURORA_PAY;

const AuroraPayPaymentInfoReadOnly = () => {
  const formatMessage = useFormatMessage();
  const { isSubmittingOrder } = useContext(CheckoutContext);
  const history = useHistory();
  const { cart } = useContext(CartContext);
  return (
    <div className="w-full">
      <div>
        <div>
          <AuroraPayImageWhite className={'h-12 p-4 rounded bg-aurora-700'} />
        </div>
        <div className="font-bold mb-2 mt-2">
          {formatMessage(messages.auroraPay)}
        </div>
        <p>Purchase Order: {cart.purchaseOrderNumber}</p>
      </div>
      <div className="flex justify-end w-full">
        <SecondaryButton
          onClick={() => {
            const state = get(history, 'location.state', {});
            history.push(PAYMENT_INFO_PATH, {
              ...state,
              editing: true
            });
          }}
          size={SecondaryButton.Size.SMALL}
          disabled={isSubmittingOrder}
        >
          {formatMessage(messages.edit)}
        </SecondaryButton>
      </div>
    </div>
  );
};

AuroraPayPaymentInfoStage.Editable = AuroraPayPaymentInfoEditable;
AuroraPayPaymentInfoStage.ReadOnly = AuroraPayPaymentInfoReadOnly;

export default AuroraPayPaymentInfoStage;
